import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import { useUserActions } from 'actions/user-actions';
import { FC, useEffect, useState } from 'react';
import { store, useAppSelector } from 'setup/store';
import UICard from 'components/lib/card/UICard';
import { IRole } from 'common/interfaces/irole';
import { useNavigate } from 'react-router-dom';
import { useAppActions } from 'actions/app-actions';
import { ErrorType, FormItem, SnackbarType } from 'common/interfaces';
import { IStore } from 'common/interfaces/istore';
import { useStoreActions } from 'actions/store-actions';
import UserSelectionList from 'components/user/user-selection-list/UserSelectionList';
import { IUser } from 'common/interfaces/iuser';
import UILogger from 'common/utils/logger';
import { toString } from 'lodash';
import { formatPhoneNumberInputText, validateFormData } from 'common/utils';
import { useAccessActions } from 'actions/access-actions';
import AccessKeys from 'common/constants/acces';
import { StoreTypeEnum } from 'common/constants/store';
import { useTranslation } from 'react-i18next';
import { DropdownItem } from 'common/constants';

type StoreDataFormProps = {
  storeId?: number;
  readonly?: boolean;
  addNew?: boolean;
};

const DEFAULT_STORE_DATA = {
  email: '',
  phoneNo: '',
} as IStore;

const FORM_FIELDS = [
  {
    itemKey: 'storeNumber',
    label: 'Store Number',
    type: 'textInput',
    minLength: 1,
    maxLength: 5,
    validationType: 'decimal',
    required: true,
  },
  {
    itemKey: 'storeName',
    label: 'Store Name',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
  },
  {
    itemKey: 'addressStreet',
    label: 'Street Address',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
  },
  {
    itemKey: 'addressCity',
    label: 'City',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
  },
  {
    itemKey: 'zipcode',
    label: 'Zipcode',
    type: 'textInput',
    minLength: 5,
    maxLength: 5,
    validationType: 'numeric',
    required: true,
  },
  {
    itemKey: 'addressState',
    label: 'State',
    type: 'textInput',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
  },
  {
    itemKey: 'franchiseeAdminId',
    label: 'Franchisee Admin',
    type: 'dropdown',
    minLength: 1,
    maxLength: 50,
    validationType: 'numeric',
    required: true,
  },
  {
    itemKey: 'storeType',
    label: 'Store Type',
    type: 'dropdown',
    minLength: 1,
    maxLength: 50,
    validationType: 'text',
    required: true,
  },
] as FormItem[];

const StoreDataForm: FC<StoreDataFormProps> = ({ storeId, readonly, addNew }) => {
  const roleIdMap = useAppSelector((state) => state.userReducer.roleIdMap);
  const roles = useAppSelector((state) => state.userReducer.roles);
  const error = useAppSelector((store) => store.storeReducer.error);
  const updateError = useAppSelector((store) => store.updateStoreReducer.error);
  const updating = useAppSelector((store) => store.updateStoreReducer.loading);
  const isUpdated = useAppSelector((store) => store.updateStoreReducer.isUpdated);
  const { hasAccess } = useAccessActions();
  const { t } = useTranslation();

  const navigate = useNavigate();
  const [_store, setStore] = useState<IStore>();
  const [_roles, setRoles] = useState<IRole[]>([] as IRole[]);
  const [_storeTypes, setStoreTypes] = useState<DropdownItem[]>([]);
  const { showSnackBar } = useAppActions();
  const { loadStoreById, updateStore, resetUpdateStoreForm } = useStoreActions();
  const { loadAllRoles } = useUserActions();
  const [formError, setFormError] = useState<ErrorType>({});
  const canManageStore = hasAccess(AccessKeys.MANAGE_STORE);

  const [_isReadOnly, setReadOnly] = useState<boolean>(!canManageStore || readonly ? true : false);

  const _loadStore = async (storeId: number) => {
    const store = await loadStoreById(storeId);
    setStore(store);
  };

  useEffect(() => {
    const storeTypes = [
      {
        value: StoreTypeEnum.WITH_GAS_STATION,
        label: t('storeType.text.' + StoreTypeEnum.WITH_GAS_STATION),
        key: StoreTypeEnum.WITH_GAS_STATION,
      },
      {
        value: StoreTypeEnum.WITHOUT_GAS_STATION,
        label: t('storeType.text.' + StoreTypeEnum.WITHOUT_GAS_STATION),
        key: StoreTypeEnum.WITHOUT_GAS_STATION,
      },
    ] as DropdownItem[];
    setStoreTypes(storeTypes);
  }, []);
  useEffect(() => {
    console.log('Mounted storeDetailsScreen.....', storeId);
    resetUpdateStoreForm();
    if (storeId) {
      _loadStore(storeId);
      loadAllRoles();
    } else if (addNew) {
      loadAllRoles();
      setStore(DEFAULT_STORE_DATA);
    }
  }, [storeId, addNew]);

  useEffect(() => {
    if (roles) {
      const applicableRoles = roles.items?.filter(
        (role) => role.roleName === 'franchisee_admin' || role.roleName === 'accountant',
      );
      setRoles(applicableRoles || []);
    }
  }, [roles]);

  const validateForm = (itemKey?: string, value?: any) => {
    const validation = validateFormData(FORM_FIELDS, _store, itemKey, value);
    setFormError({ ...validation.error });
    return validation.valid;
  };

  const _handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const valid = validateForm();
    if (valid) {
      if (_store) {
        const resp = await updateStore(_store);
        if (resp && storeId) {
          _loadStore(storeId);
          showSnackBar(SnackbarType.SUCCESS, 'Updated successfully!');
        } else if (resp) {
          setStore(DEFAULT_STORE_DATA);
          showSnackBar(SnackbarType.SUCCESS, 'Store added successfully! Name: ' + resp.storeName);
          navigate('/stores');
        }
      }
    }
  };

  const _handleUserSelection = (user: IUser | undefined) => {
    UILogger.log('_handleUserSelection:', user);
    if (store && user && user.id > 0) {
      setStore({ ..._store, franchiseeAdminId: user.id } as IStore);
    }
  };

  const _handleCancel = () => {
    navigate('/stores');
  };

  const handleStoreTypeChange = (event: SelectChangeEvent) => {
    if (event.target.value && _store) {
      setStore({ ..._store, storeType: event.target.value });
      validateForm('storeType', event.target.value);
    }
  };

  return (
    <div>
      {error ? <Alert severity="error"> {error.msg}</Alert> : null}
      <Box
        component="form"
        sx={{
          '& .MuiTextField-root': { m: 1.5, width: '50ch' },
          '& .select': { m: 1.5, width: '50ch' },
          '& .selectLabel': { m: 1.5 },
          '& .MuiButton-root': { m: 1.5 },
          //   '& .MuiSelect-root': { m: 1, width: '50ch' },
        }}
        noValidate
        autoComplete="off"
      >
        {(roleIdMap && _store?.id) || (addNew && roleIdMap && _store) ? (
          <div>
            <UICard>
              <FormControl fullWidth>
                <TextField
                  disabled={_isReadOnly || !addNew}
                  required
                  id="outlined-required"
                  label="Store Number"
                  error={formError['storeNumber'] ? true : false}
                  helperText={formError['storeNumber'] ? formError['storeNumber'] : ''}
                  value={_store?.storeNumber || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const storeNumber = event.target.value ? Number(event.target.value) : 0;
                    setStore({ ..._store, storeNumber: storeNumber });
                    validateForm('storeNumber', storeNumber);
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_isReadOnly}
                  id="outlined-disabled"
                  label="Store Name"
                  error={formError['storeName'] ? true : false}
                  helperText={formError['storeName'] ? formError['storeName'] : ''}
                  value={_store?.storeName || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setStore({ ..._store, storeName: event.target.value });
                    validateForm('storeName', event.target.value);
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_isReadOnly}
                  id="outlined-disabled"
                  label="Street Address"
                  error={formError['addressStreet'] ? true : false}
                  helperText={formError['addressStreet'] ? formError['addressStreet'] : ''}
                  value={_store?.addressStreet || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setStore({ ..._store, addressStreet: event.target.value });
                    validateForm('addressStreet', event.target.value);
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_isReadOnly}
                  id="outlined-disabled"
                  label="City"
                  error={formError['addressCity'] ? true : false}
                  helperText={formError['addressCity'] ? formError['addressCity'] : ''}
                  value={_store?.addressCity || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setStore({ ..._store, addressCity: event.target.value });
                    validateForm('addressCity', event.target.value);
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_isReadOnly}
                  id="outlined-disabled"
                  label="State"
                  error={formError['addressState'] ? true : false}
                  helperText={formError['addressState'] ? formError['addressState'] : ''}
                  value={_store?.addressState || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setStore({ ..._store, addressState: event.target.value });
                    validateForm('addressState', event.target.value);
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_isReadOnly}
                  id="outlined-disabled"
                  label="Zipcode"
                  type="number"
                  slotProps={{ htmlInput: { maxLength: 5 } }}
                  error={formError['zipcode'] ? true : false}
                  helperText={formError['zipcode'] ? formError['zipcode'] : ''}
                  value={_store?.zipcode || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setStore({ ..._store, zipcode: event.target.value });
                    validateForm('zipcode', event.target.value);
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_isReadOnly}
                  id="outlined-disabled"
                  label="Email"
                  value={_store?.email || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setStore({ ..._store, email: event.target.value });
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <TextField
                  disabled={_isReadOnly}
                  id="outlined-disabled"
                  label="Phone"
                  type="tel"
                  value={_store?.phoneNo || ''}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    const val = event.target.value;
                    const formattedText = formatPhoneNumberInputText(val, _store?.phoneNo);
                    setStore({ ..._store, phoneNo: formattedText });
                  }}
                />
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="storeForm_StoreType-select-label" className="selectLabel">
                  Store Type
                </InputLabel>
                <Select
                  className="select"
                  disabled={_isReadOnly}
                  labelId="storeForm_StoreType-select-label"
                  id="storeForm_StoreType-select"
                  value={(_store.storeType || '') + ''}
                  label="Store Type"
                  // placeholder="Select a role"

                  error={formError['storeType'] ? true : false}
                  // helperText={formError['roleId'] ? formError['roleId'] : ''}
                  onChange={handleStoreTypeChange}
                >
                  {_storeTypes?.map((st) => (
                    <MenuItem key={'storeType:' + st.value} value={st.value}>
                      {st.label}
                    </MenuItem>
                  ))}
                </Select>
                <Typography
                  style={{
                    color: '#d32f2f',
                    fontSize: '0.75rem',
                    marginLeft: '28px',
                    fontWeight: '400',
                    marginTop: '-10px',
                  }}
                >
                  {formError['storeType'] ? formError['storeType'] : ''}
                </Typography>
              </FormControl>
              {!_isReadOnly ? (
                <FormControl fullWidth>
                  <UserSelectionList
                    label="Franchisee Admin"
                    onUserSelected={_handleUserSelection}
                    selectedUserId={_store.franchiseeAdminId}
                    disabled={_isReadOnly || !addNew}
                  />
                  <Typography
                    style={{
                      color: '#d32f2f',
                      fontSize: '0.75rem',
                      marginLeft: '28px',
                      fontWeight: '400',
                      marginTop: '-10px',
                    }}
                  >
                    {formError['franchiseeAdminId'] ? formError['franchiseeAdminId'] : ''}
                  </Typography>
                </FormControl>
              ) : null}
              {updateError ? <Alert severity="error">{updateError?.msg}</Alert> : null}
              {/* {isUpdated ? <Alert severity="success">{'Updated successfully!'}</Alert> : null} */}
              <Button
                variant="contained"
                onClick={_handleFormSubmit}
                style={{ marginTop: '30px' }}
                disabled={_isReadOnly}
              >
                Submit
              </Button>
              <Button
                disabled={updating}
                variant="contained"
                onClick={_handleCancel}
                style={{ marginTop: '30px' }}
                color="error"
              >
                Cancel
              </Button>
            </UICard>
          </div>
        ) : null}
      </Box>
    </div>
  );
};

export default StoreDataForm;
